import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, KeyRound, Plus, Trash2, ChevronRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import HostLayout from '@/components/mafia/HostLayout';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HostGamesPage() {
  const navigate = useNavigate();
  const { host, loading } = useMafiaAuth();
  const [games, setGames] = useState([]);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);

  useEffect(() => { if (!loading && !host) navigate('/mafia/login', { replace: true }); }, [loading, host, navigate]);
  useEffect(() => {
    if (!host) return;
    refresh();
  }, [host]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    try {
      const r = await fetch(`${API}/api/mafia/games`);
      const data = await r.json();
      setGames(data.games || []);
    } catch { /* ignore */ }
  };

  const remove = async (id) => {
    if (!window.confirm('Удалить игру и её историю?')) return;
    setBusy(id);
    try {
      const r = await fetch(`${API}/api/mafia/games/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error();
      toast.success('Game deleted');
      await refresh();
    } catch { toast.error('Could not delete'); }
    finally { setBusy(null); }
  };

  if (loading || !host) return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;

  const FILTERS = [
    { k: 'all', l: `Все (${games.length})` },
    { k: 'active', l: `Активные (${games.filter(g => g.status !== 'ended').length})` },
    { k: 'ended', l: `Завершённые (${games.filter(g => g.status === 'ended').length})` },
  ];
  const filtered = games.filter(g => filter === 'all' || (filter === 'active' ? g.status !== 'ended' : g.status === 'ended'));

  return (
    <HostLayout title="Игры" subtitle="Все столы"
      actions={
        <button onClick={() => navigate('/mafia/new')} data-testid="games-new-btn" className="ms-btn ms-btn-primary">
          <Plus size={13} /> Новая игра
        </button>
      }
    >
      <div className="flex items-center gap-1 ms-surface-1 ms-border p-1 mb-6 w-fit" style={{ borderRadius: 'var(--ms-radius-md)' }}>
        {FILTERS.map(opt => (
          <button key={opt.k} onClick={() => setFilter(opt.k)}
            className={`px-4 py-1.5 text-xs font-medium transition-colors ${filter === opt.k ? 'ms-text' : 'ms-text-2 hover:ms-text'}`}
            style={filter === opt.k ? { background: 'var(--ms-surface-3)', borderRadius: 'var(--ms-radius-sm)' } : { borderRadius: 'var(--ms-radius-sm)' }}>
            {opt.l}
          </button>
        ))}
      </div>

      <section className="ms-card" data-testid="games-table">
        {filtered.length === 0 ? (
          <p className="px-5 py-12 text-sm ms-text-2 text-center">Нет игр по этому фильтру.</p>
        ) : (
          <div className="ms-divide">
            {filtered.map(g => (
              <div key={g.id} className="grid grid-cols-12 items-center px-5 py-3 hover:bg-[var(--ms-surface-2)] transition-colors group" data-testid={`game-row-${g.id}`}>
                <button onClick={() => navigate(`/mafia/game/${g.id}`)} className="col-span-5 text-left min-w-0">
                  <p className="text-sm ms-text truncate group-hover:ms-text-red transition-colors">{g.name || 'Без названия'}</p>
                  <p className="text-[11px] ms-text-3 mt-0.5">{g.created_at ? new Date(g.created_at).toLocaleString('ru-RU') : '—'}</p>
                </button>
                <span className="col-span-2 text-xs ms-text-2">{g.players?.length || 0} мест</span>
                <span className="col-span-2 text-xs ms-text-3 capitalize">{g.phase || '—'}</span>
                <span className="col-span-2 text-xs"><span className={`ms-chip ${g.status === 'ended' ? '' : 'ms-chip-red'}`}>{g.status}</span></span>
                <div className="col-span-1 flex items-center justify-end gap-1">
                  <button onClick={() => navigate(`/mafia/game/${g.id}`)} className="ms-btn ms-btn-ghost ms-btn-sm" title="Open">
                    <ChevronRight size={14} />
                  </button>
                  <button onClick={() => remove(g.id)} disabled={busy === g.id} className="ms-btn ms-btn-ghost ms-btn-sm text-rose-400" title="Delete">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </HostLayout>
  );
}

export function HostSettingsPage() {
  const navigate = useNavigate();
  const { host, loading, setHost } = useMafiaAuth();
  const [name, setName] = useState('');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!loading && !host) navigate('/mafia/login', { replace: true }); }, [loading, host, navigate]);
  useEffect(() => { if (host) setName(host.name || ''); }, [host]);

  const saveName = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/mafia/auth/host/me`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setHost && setHost(data.host);
      toast.success('Name updated');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (!current || !next) { toast.error('Fill both password fields'); return; }
    if (next !== confirm) { toast.error('Passwords do not match'); return; }
    if (next.length < 8) { toast.error('Password must be ≥ 8 chars'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/mafia/auth/host/me`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Failed');
      }
      setCurrent(''); setNext(''); setConfirm('');
      toast.success('Password updated');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  if (loading || !host) return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;

  return (
    <HostLayout title="Настройки" subtitle="Аккаунт ведущего">
      <div className="max-w-2xl space-y-6" data-testid="host-settings-page">
        <section className="ms-card">
          <div className="px-5 py-4 ms-border-b"><p className="ms-kicker">Профиль</p></div>
          <div className="ms-divide">
            <div className="px-5 py-4 flex items-center justify-between gap-4">
              <span className="text-xs ms-text-2">Email</span>
              <span className="text-sm ms-text">{host.email}</span>
            </div>
            <div className="px-5 py-4 space-y-2">
              <label className="text-xs font-medium ms-text">Отображаемое имя</label>
              <div className="flex gap-2">
                <input value={name} onChange={(e) => setName(e.target.value)} className="ms-input flex-1" data-testid="host-name-input" />
                <button onClick={saveName} disabled={saving || !name.trim()} className="ms-btn ms-btn-primary ms-btn-sm" data-testid="host-name-save">
                  <Save size={12} /> Сохранить
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="ms-card">
          <div className="px-5 py-4 ms-border-b flex items-center gap-2">
            <KeyRound size={13} className="ms-text-red" />
            <p className="ms-kicker">Сменить пароль</p>
          </div>
          <div className="p-5 space-y-3">
            <PasswordField label="Текущий пароль" value={current} onChange={setCurrent} show={show} onToggle={() => setShow(s => !s)} testid="host-pw-current" />
            <PasswordField label="Новый пароль" value={next} onChange={setNext} show={show} testid="host-pw-new" />
            <PasswordField label="Подтвердите новый пароль" value={confirm} onChange={setConfirm} show={show} testid="host-pw-confirm" />
            <p className="text-[11px] ms-text-3">Минимум 8 символов. Сессия не прерывается.</p>
            <button onClick={changePassword} disabled={saving} className="ms-btn ms-btn-primary" data-testid="host-pw-save">
              <Save size={13} /> {saving ? 'Обновление…' : 'Обновить пароль'}
            </button>
          </div>
        </section>
      </div>
    </HostLayout>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, testid }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium ms-text">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="ms-input pr-10"
        />
        {onToggle && (
          <button type="button" onClick={onToggle} className="absolute right-2 top-1/2 -translate-y-1/2 ms-text-3 hover:ms-text">
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        )}
      </div>
    </div>
  );
}
